import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { ModalShell } from '@/components/ui/ModalShell'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { morphToast } from '@/components/ui/Toast'
import { useExportActions } from '@/hooks/useExportActions'
import { useModalStore } from '@/hooks/useModalStore'
import { useDiagramStore, useSettingsStore } from '@/stores'
import { useCommitsStore } from '@/stores/commitsStore'
import { useDraftStore } from '@/stores/draftStore'

export function ExportModal() {
  const { t } = useTranslation()
  const open = useModalStore((state) => state.exportOpen)
  const close = useModalStore((state) => state.closeExport)
  const { exportSql, exportDictionary, exportVersionComparison, exportPng, exportSvg } = useExportActions()
  const projectName = useDiagramStore((state) => state.projectName)
  const commits = useCommitsStore((state) => state.commits)
  const loadCommits = useCommitsStore((state) => state.loadCommits)
  const currentProjectId = useDraftStore((state) => state.currentProjectId)
  // Commits arrive newest-first; reverse to oldest-first so the defaults pick the latest two.
  const versions = useMemo(
    () => [...commits].reverse().map((commit) => ({ id: String(commit.id), name: commit.name, timestamp: commit.created_at })),
    [commits],
  )
  const language = useSettingsStore((state) => state.language)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [fileBaseName, setFileBaseName] = useState('')
  const [compareBaseVersionId, setCompareBaseVersionId] = useState('')
  const [compareTargetVersionId, setCompareTargetVersionId] = useState('')

  useEffect(() => {
    if (open && currentProjectId) void loadCommits(currentProjectId)
  }, [open, currentProjectId, loadCommits])

  useEffect(() => {
    if (open) {
      setFileBaseName(projectName || 'MorphERD')
      const fallbackBase = versions[versions.length - 2]?.id ?? ''
      const fallbackTarget = versions[versions.length - 1]?.id ?? ''
      setCompareBaseVersionId(fallbackBase)
      setCompareTargetVersionId(fallbackTarget)
    }
  }, [open, projectName, versions])

  const runAction = async (
    id: string,
    action: () => unknown,
    successMessage?: string,
    loadingMessage?: string,
  ) => {
    setBusyId(id)
    const loadingToastId = loadingMessage ? morphToast.loading(loadingMessage) : null
    try {
      const result = await action()
      if (typeof result === 'object' && result && 'ok' in result && !result.ok) {
        if ('reason' in result && result.reason === 'not-enough-versions') {
          morphToast.warning(t('export.compareUnavailable'))
          return
        }
        if ('reason' in result && result.reason === 'invalid-version-selection') {
          morphToast.warning(t('export.compareInvalidSelection'))
          return
        }
        if ('reason' in result && result.reason === 'empty-diagram') {
          morphToast.warning(t('export.imageNoTables'))
          return
        }
      }
      if (successMessage) {
        morphToast.success(successMessage)
      }
      close()
    } catch (error) {
      const messageKey =
        error instanceof Error && error.message === 'EXPORT_CANVAS_TOO_LARGE'
          ? 'export.imageTooLarge'
          : 'export.imageError'
      morphToast.error(t(messageKey))
    } finally {
      if (loadingToastId) morphToast.dismiss(loadingToastId)
      setBusyId(null)
    }
  }

  const runNamedAction = async (
    id: string,
    action: () => unknown,
    successMessage?: string,
    loadingMessage?: string,
  ) => {
    if (!fileBaseName.trim()) {
      morphToast.error(t('export.fileNameRequired'))
      return
    }

    await runAction(id, action, successMessage, loadingMessage)
  }

  const versionOptions = versions.map((version) => ({
    value: version.id,
    label: version.name,
    badge: new Intl.DateTimeFormat(language).format(new Date(version.timestamp)),
  }))

  const options = [
    { id: 'sql', title: t('export.sqlTitle'), description: t('export.sqlDesc'), onClick: () => runNamedAction('sql', () => exportSql(fileBaseName), t('export.sqlDone')) },
    {
      id: 'png',
      title: t('export.pngTitle'),
      description: t('export.pngDesc'),
      onClick: () => runNamedAction('png', () => exportPng(fileBaseName), t('export.imageDone'), t('export.exportingPng')),
    },
    {
      id: 'svg',
      title: t('export.svgTitle'),
      description: t('export.svgDesc'),
      onClick: () => runNamedAction('svg', () => exportSvg(fileBaseName), t('export.svgDone'), t('export.exportingSvg')),
    },
    { id: 'dict', title: t('export.dictTitle'), description: t('export.dictDesc'), onClick: () => runNamedAction('dict', () => exportDictionary(fileBaseName), t('export.dictDone')) },
    { id: 'compare', title: t('export.compareTitle'), description: t('export.compareDesc'), onClick: () => runNamedAction('compare', () => exportVersionComparison({ overrideName: fileBaseName, baseVersionId: compareBaseVersionId, targetVersionId: compareTargetVersionId }), t('export.compareDone')) },
  ]

  return (
    <ModalShell
      open={open}
      onClose={close}
      title={t('export.title')}
      widthClassName="max-w-3xl"
      footer={<Button variant="ghost" onClick={close}>{t('common.close')}</Button>}
    >
      <div className="mb-4">
        <label className="mb-1 block text-[10px] uppercase tracking-[0.3px]" style={{ color: 'var(--text-dim)' }}>
          {t('export.fileName')}
        </label>
        <input
          type="text"
          value={fileBaseName}
          onChange={(event) => {
            setFileBaseName(event.target.value)
          }}
          className="morph-input max-w-[320px]"
        />
      </div>
      <div className="space-y-3">
        {options.map((option) => (
          <div
            key={option.id}
            className="rounded-xl border px-4 py-4"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}
          >
            <button
              type="button"
              onClick={() => {
                void option.onClick()
              }}
              disabled={busyId !== null}
              className="w-full text-left disabled:opacity-60"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold" dangerouslySetInnerHTML={{ __html: option.title }} />
                  </div>
                  <div className="mt-1 text-[11px] leading-5" style={{ color: 'var(--text-dim)' }}>{option.description}</div>
                </div>
                <span className="text-[11px]" style={{ color: 'var(--accent)' }}>
                  {busyId === option.id ? '…' : t('common.download')}
                </span>
              </div>
            </button>
            {option.id === 'compare' ? (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <MorphSelect
                  label={t('export.compareFrom')}
                  ariaLabel={t('export.compareFrom')}
                  value={compareBaseVersionId}
                  options={versionOptions}
                  onChange={(nextVersionId) => {
                    setCompareBaseVersionId(nextVersionId ?? '')
                  }}
                />
                <MorphSelect
                  label={t('export.compareTo')}
                  ariaLabel={t('export.compareTo')}
                  value={compareTargetVersionId}
                  options={versionOptions}
                  onChange={(nextVersionId) => {
                    setCompareTargetVersionId(nextVersionId ?? '')
                  }}
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </ModalShell>
  )
}
